import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  Card,
  TextStyle,
  ActionList,
  Popover,
  Banner
} from '@superops/ui-design-system';
import ChartContext from '@nile/reports/src/modules/charts/contexts/Chart';
import {
  getPossibleChartTypes,
  getDefaultChartType,
  getTransformedChartData,
  CHART_ICON
} from '@nile/reports/src/modules/charts/utils/chart-transformation';
import ReportsContext from '@nile/reports/src/modules/home/contexts/Reports';
import {
  BLACKLISTED_ENTITIES,
  NUMBER,
  NUMBER_CHART,
  SUMMARY_TABLE
} from '@nile/reports/src/modules/charts/constants';
import { VIEW_MODE } from '@nile/reports/src/modules/home/constants';
import { preFormatFilterObject } from '@nile/reports/src/modules/component/helper';
import { AccountClient } from '@nile/graphql';
import {
  Loader,
  withErrorBoundary,
  toast,
  getTranslatedText
} from '@nile/components';
import { Image } from '@nile/components/src/Components/Nodata';
import envConfig from '@nile/home/src/Config/env';
import { PREVIEW_CHART } from '@nile/reports/src/modules/charts/graphql';
import { SizeMe } from 'react-sizeme';
import { injectIntl, FormattedMessage, useIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { cloneDeep, isArray, isEmpty } from 'lodash';
import dayjs from 'dayjs';
import EChart from '../EChart';
import NumberChart from '../NumberChart';
import SummaryTable from '../SummaryTable';
import { ChartDetailWrapper } from './styles';
import DataTable from '../DataTable';

const ChartDetail = (props) => {
  const context = useContext(ChartContext);
  const {
    isViewOnly,
    submitActionLoading,
    validateChartForm,
    applyFilter,
    originalData,
    numberChartCallback,
    numberChartTooltip
  } = props;

  const intl = useIntl();

  const reportsContext = useContext(ReportsContext);
  const [originalChartData, setOriginalChartData] = useState();

  const [globalFilter, setGlobalFilter] = useState(
    reportsContext.reportCondition[reportsContext.activeView]
  );

  const [chartRawData, setChartRawData] = useState();

  const [showPreview, setShowPreview] = useState(false);

  const [chartData, setChartData] = useState();

  const [loading, setLoading] = useState(true);

  const [chartLoading, setChartLoading] = useState(true);

  const [previewLoading, setPreviewLoading] = useState(false);

  const [active, setActive] = useState(false);

  const toggleActive = useCallback(
    () => setActive((prevState) => !prevState),
    []
  );

  const possibleChartType = getPossibleChartTypes(
    context.value.metrics?.length,
    context.value.groupBy?.length,
    context?.value?.entity
  );

  const chartType =
    (possibleChartType.find(
      (type) => type.name === context.value.defaultChartType
    ) &&
      context.value.defaultChartType) ||
    getDefaultChartType(
      context.value.metrics?.length,
      context.value.groupBy?.length
    );

  const transformData = async (chartDataa) => {
    return getTransformedChartData(
      chartDataa,
      context.value.metrics?.length,
      context.value.groupBy?.length,
      chartType
    );
  };

  const setTransformedData = (data) => {
    const transformed = transformData(data);
    transformed
      ?.then((formatteddata) => setChartData(formatteddata))
      .catch((err) => {})
      .finally(() => setLoading(false));
  };

  const setLoaders = () => {
    setChartLoading(false);
    setPreviewLoading(false);
  };

  const getChartData = async (isPreview = false) => {
    setChartLoading(true);

    if (context.value.chartId || isPreview) {
      let input = isPreview
        ? {
            groupBy: context?.value?.groupBy,
            metrics: context?.value?.metrics,
            entity: context?.value?.entity
          }
        : {
            chart: {
              chartId: context?.value?.chartId
            }
          };
      if (reportsContext.reportViewMode === VIEW_MODE && applyFilter) {
        const { operands, reportCondition } = await preFormatFilterObject({
          entity: context?.value?.entity,
          filterObj: cloneDeep(globalFilter)
        });

        input = {
          ...input,
          condition:
            (reportCondition &&
              !isEmpty(operands) && {
                joinOperator: 'AND',
                operands
              }) ||
            null
        };
      }
      setPreviewLoading(true);
      AccountClient.query({
        query: PREVIEW_CHART,
        variables: {
          PreviewChartInput: input
        },
        fetchPolicy: 'network-only'
      })
        .then(({ data, errors }) => {
          if (data?.previewChart) {
            if (isPreview) setShowPreview(true);
            if (!isPreview) setOriginalChartData(data?.previewChart);
            setChartRawData(data?.previewChart);
            const transformed = transformData(data?.previewChart);
            transformed
              .then((formatteddata) => setChartData(formatteddata))
              .catch((err) => {})
              .finally(() => {
                setLoaders();
              });
          } else {
            if (errors.length) toast('error', 'common.error');
            setChartRawData();
            setChartData();
            setLoaders();
            setLoading(false);
          }
        })
        .catch((err: any) => {
          toast('error', 'common.error');
          setLoaders();
        });
    }
  };

  const previewChart = async () => {
    const noError = await validateChartForm();
    if (noError) {
      getChartData(true);
    }
  };

  const getUpdatedMetric = () => {
    const modifiedMetric = context.value.metrics.map((el) => {
      if (el?.metricId) {
        return el;
      }
      const metData = chartRawData?.data?.[0]?.data;
      if (context.value.groupBy.length === 1) {
        return (
          (metData &&
            metData?.find(
              (met) => met.label === el.name && met.attribute === el.attribute
            )) ||
          el
        );
      }
      return (
        (metData &&
          metData?.[0]?.data &&
          metData?.[0]?.data.find(
            (met) => met.label === el.name && met.attribute === el.attribute
          )) ||
        el
      );
    });

    return modifiedMetric;
  };

  const renderData = (type) => {
    const defaultType = type;
    if (!chartData) {
      return (
        <ChartDetailWrapper className="fh fx jctr actr ">
          <div>
            <div className="flex actr jctr">
              <Image
                url={`url(${envConfig.imageRoot}svg/chartEmpty.svg)`}
                height="150px"
                width="200px"
              />
            </div>
            <div className="flex actr jctr mt10">
              <TextStyle variation="subdued">
                <FormattedMessage id="reports.chart.data.empty" />
              </TextStyle>
            </div>
          </div>
        </ChartDetailWrapper>
      );
    }
    if (defaultType === NUMBER) {
      return (
        <NumberChart
          data={chartData}
          btnCallback={(index) => {
            numberChartCallback({
              data: context.value,
              index,
              originalData,
              tableTitle: context.headerValue?.name
            });
          }}
          type={NUMBER_CHART.SUB_TYPE.BUTTON}
          tooltipContent={getTranslatedText(intl, 'common.view.data')}
        />
      );
    }
    if (defaultType === SUMMARY_TABLE) {
      const updatedMetric = getUpdatedMetric();
      return (
        <SummaryTable
          data={chartData}
          groups={context.value.groupBy}
          metrics={updatedMetric}
          entity={context.value.entity}
          chartRawData={chartRawData}
        />
      );
    }
    return (
      <SizeMe monitorHeight monitorWidth>
        {({ size }) => (
          <div style={{ height: '90%' }}>
            <EChart
              chartOptions={chartData}
              internalName={context?.value?.internalName}
              opts={{ height: size.height, width: size.width, renderer: 'svg' }}
            />
          </div>
        )}
      </SizeMe>
    );
  };

  const chartTypeHandler = (type) => {
    if (type === context.value.defaultChartType) return;
    setLoading(true);
    context.updateContext({ ...context.value, defaultChartType: type });
    setActive(false);
  };

  const getDataTableHide = () => {
    if (
      BLACKLISTED_ENTITIES.includes(context.value.entity) &&
      !context.value.chartId
    ) {
      return true;
    }
    return false;
  };

  const hideDataTable = getDataTableHide();

  useEffect(() => {
    if (showPreview) {
      setShowPreview(false);
    }
  }, [context.value.metrics, context.value.groupBy, context.value.entity]);

  useEffect(() => {
    setTransformedData(chartRawData);
  }, [chartRawData, context.value.defaultChartType]);

  useEffect(() => {
    setShowPreview(false);
    getChartData();
  }, [JSON.stringify(originalData)]);

  const sameConfig = context.isSameData();

  useEffect(() => {
    if (
      !globalFilter &&
      reportsContext.reportCondition[reportsContext.activeView]
    ) {
      getChartData(!sameConfig);
    }
  }, [globalFilter]);

  useEffect(() => {
    if (sameConfig) {
      setChartRawData(originalChartData);
    }
  }, [sameConfig]);

  if (!sameConfig && !showPreview) {
    let emptyCondition = false;
    if (context.value.entity === 'AssetSoftwareInventory') {
      emptyCondition = context.value.metrics
        .map((el) => el.filterCondition)
        .includes(null);
    }
    return (
      <ChartDetailWrapper isFilterApplied={globalFilter}>
        <div className="card">
          <Card AdaptHeight padding="sizeSmall">
            <div className="chartCard fx jctr col">
              <div className="flex actr jctr">
                <Image
                  url={`url(${envConfig.imageRoot}svg/EmptyChartNew.svg)`}
                  height="150px"
                  width="200px"
                />
              </div>
              <div className="flex actr jctr mt10">
                <Button
                  primary
                  onClick={() => previewChart()}
                  loading={submitActionLoading || previewLoading}
                  disabled={emptyCondition}
                >
                  <FormattedMessage id="reports.widget.preview.btn" />
                </Button>
              </div>
              {emptyCondition && (
                <div className="fx jctr mt-10">
                  <FormattedMessage id="reports.softwareInventory.condition.mandate.info" />
                </div>
              )}
            </div>
          </Card>
        </div>
      </ChartDetailWrapper>
    );
  }

  const bannerContent = () => {
    const dataString = [];
    const client =
      globalFilter.find((el) => el.attribute === 'client')?.label || null;

    if (client) {
      dataString.push(client);
    }
    const date =
      globalFilter.find((el) => el.attribute === 'createdTime')?.value || null;

    const displayDate =
      date &&
      (isArray(date)
        ? date.map((el) => dayjs(el).format('DD MMM YYYY')).join(' - ')
        : getTranslatedText(intl, date));
    if (displayDate) {
      dataString.push(displayDate);
    }

    return (
      <div className="fx actr g10">
        <TextStyle>{`${dataString.join('  |  ')}`}</TextStyle>
        <Button
          plain
          onClick={() => {
            setGlobalFilter(null);
          }}
        >
          {getTranslatedText(intl, 'common.remove')}
        </Button>
      </div>
    );
  };

  return (
    <ChartDetailWrapper>
      <div className="card fixed-height dataCard">
        <Card AdaptHeight padding="sizeSmall">
          <div
            className={`${globalFilter && applyFilter ? 'fx' : ''} actr g10`}
          >
            {globalFilter && applyFilter && (
              <div className="fx2">
                <Banner
                  status="info"
                  title={getTranslatedText(
                    intl,
                    'reports.widget.banner.globalFilter.title'
                  )}
                >
                  {bannerContent()}
                </Banner>
              </div>
            )}
            <div className="flex jend">
              {!!possibleChartType?.length && (
                <div superid="chartType">
                  <Popover
                    active={active}
                    preferredAlignment="right"
                    activator={
                      <>
                        <Button
                          onClick={toggleActive}
                          icon={
                            <SVGIcon
                              name={
                                CHART_ICON[
                                  possibleChartType
                                    .map((chrt) => ({
                                      ...chrt,
                                      value: chrt.name
                                    }))
                                    .find((chrt) => chrt.name === chartType)
                                    ?.name || 'DOUGHNUT'
                                ]
                              }
                              size="lg"
                            />
                          }
                        />
                      </>
                    }
                    onClose={toggleActive}
                  >
                    <ActionList
                      actionRole="menuitem"
                      items={possibleChartType.map((chrt) => ({
                        ...chrt,
                        content: (
                          <div title={chrt?.label} className="fx actr g10">
                            <SVGIcon
                              name={CHART_ICON[chrt.name || 'DOUGHNUT']}
                              size="lg"
                            />
                            <TextStyle>{chrt.label}</TextStyle>
                          </div>
                        ),
                        value: chrt.name,
                        onAction: () => chartTypeHandler(chrt.name)
                      }))}
                    />
                  </Popover>
                </div>
              )}
              {!isViewOnly && (
                <div className="ml-10 chart-full-toggle">
                  <Button
                    onClick={() =>
                      context.setIsFullScreen(!context.isFullScreen)
                    }
                    icon={
                      <SVGIcon
                        name={context.isFullScreen ? 'minimize' : 'FullScreen'}
                      />
                    }
                  />
                </div>
              )}
            </div>
          </div>
          <div className="chart-holder table">
            {loading || chartLoading ? (
              <div className="fx full-height">
                <Loader />
              </div>
            ) : (
              renderData(
                (getPossibleChartTypes(
                  context.value.metrics?.length,
                  context.value.groupBy?.length
                ).find(
                  (type) => type.name === context.value.defaultChartType
                ) &&
                  context.value.defaultChartType) ||
                  getDefaultChartType(
                    context.value.metrics?.length,
                    context.value.groupBy?.length
                  )
              )
            )}
          </div>
        </Card>
      </div>
      {hideDataTable ? <></> : <DataTable />}
    </ChartDetailWrapper>
  );
};

export default injectIntl(withErrorBoundary(ChartDetail));
